import React, { useCallback, useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import LinkIcon from '@material-ui/icons/Link';

const useStyles = makeStyles(theme => ({
  tableHead: {
    backgroundColor: theme.palette.background.default,
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  nameCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
  },
  dataCell: {
    fontSize: '0.82rem',
    color: theme.palette.text.secondary,
  },
  boundChip: {
    backgroundColor: theme.palette.success?.main ?? '#4caf50',
    color: '#fff',
    height: 22,
    fontSize: '0.75rem',
  },
  unboundChip: {
    height: 22,
    fontSize: '0.75rem',
  },
  scopeText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    display: 'block',
    marginTop: 2,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.85rem',
  },
  emptyBox: {
    padding: theme.spacing(2),
  },
  actionCell: {
    width: 120,
    textAlign: 'right',
  },
}));

const SUBTYPE_OVERRIDES: Record<string, string> = {
  'azure-key-vault': 'Key Vault',
  'azure-storage-account': 'Storage Account',
  'azure-data-factory': 'Data Factory',
};

function formatSubType(subType: string): string {
  return SUBTYPE_OVERRIDES[subType] ?? subType
    .replace(/^azure-/, '')
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface ResourceRow {
  entityRef: string;
  name: string;
  title: string;
  subType: string;
  boundComponents: string[];
  vaultId: string;
  vaultUri: string;
  scopeName?: string;
}

export function AzureBindResourceCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const componentRef = `component:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const compSpec = entity.spec as Record<string, any>;
  const compDependsOn: string[] = compSpec?.dependsOn ?? [];
  const environmentRef = compDependsOn.find(r => r.toLowerCase().startsWith('azureenvironment:'));

  const [resources, setResources] = useState<ResourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Bind/unbind action state
  const [actionRef, setActionRef] = useState<string | null>(null);
  const [, setActionType] = useState<'bind' | 'unbind' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Unbind confirmation dialog
  const [confirmUnbind, setConfirmUnbind] = useState<ResourceRow | null>(null);

  const loadResources = useCallback(async () => {
    if (!environmentRef) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError('');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${baseUrl}/environments/available-resources?environmentRef=${encodeURIComponent(environmentRef)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as any;
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      const data: ResourceRow[] = await res.json();
      setResources(data);
    } catch (err: any) {
      setLoadError(err.message ?? 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, [environmentRef, discoveryApi, identityApi]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleBind = async (row: ResourceRow) => {
    setActionRef(row.entityRef);
    setActionType('bind');
    setActionError('');
    setActionLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/components/bind-resource`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ componentRef, resourceRef: row.entityRef }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as any;
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      await loadResources();
    } catch (err: any) {
      setActionError(err.message ?? 'Bind failed');
    } finally {
      setActionLoading(false);
      setActionRef(null);
      setActionType(null);
    }
  };

  const handleUnbindConfirmed = async () => {
    if (!confirmUnbind) return;
    const row = confirmUnbind;
    setConfirmUnbind(null);
    setActionRef(row.entityRef);
    setActionType('unbind');
    setActionError('');
    setActionLoading(true);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${baseUrl}/components/unbind-resource`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ componentRef, resourceRef: row.entityRef }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText })) as any;
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      await loadResources();
    } catch (err: any) {
      setActionError(err.message ?? 'Unbind failed');
    } finally {
      setActionLoading(false);
      setActionRef(null);
      setActionType(null);
    }
  };

  if (!environmentRef) {
    return (
      <InfoCard title="Resource Bindings" variant="gridItem">
        <Box className={classes.emptyBox}>
          <Typography variant="body2" color="textSecondary">
            This application has no Azure Environment in its <code>spec.dependsOn</code>.
            Deploy it to an environment first.
          </Typography>
        </Box>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Resource Bindings" variant="gridItem">
      {loading && <Progress />}

      {!loading && loadError && (
        <Box className={classes.emptyBox}>
          <Typography className={classes.errorText}>{loadError}</Typography>
          <Button size="small" onClick={loadResources} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </Box>
      )}

      {!loading && !loadError && resources.length === 0 && (
        <Box className={classes.emptyBox}>
          <Typography variant="body2" color="textSecondary">
            No resources found for this environment. Create a resource using the scaffolder and select the same environment as this application.
          </Typography>
        </Box>
      )}

      {!loading && !loadError && resources.length > 0 && (
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.headCell}>Resource</TableCell>
              <TableCell className={classes.headCell}>Type</TableCell>
              <TableCell className={classes.headCell}>Status</TableCell>
              <TableCell className={classes.actionCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {resources.map(row => {
              const isBoundToThis = row.boundComponents.some(
                c => c.toLowerCase() === componentRef.toLowerCase(),
              );
              const otherBindings = row.boundComponents.filter(
                c => c.toLowerCase() !== componentRef.toLowerCase(),
              );
              const isActioning = actionLoading && actionRef === row.entityRef;

              return (
                <TableRow key={row.entityRef}>
                  <TableCell className={classes.nameCell}>{row.title || row.name}</TableCell>
                  <TableCell className={classes.dataCell}>
                    {formatSubType(row.subType)}
                  </TableCell>
                  <TableCell>
                    {isBoundToThis ? (
                      <>
                        <Chip label="Bound" size="small" className={classes.boundChip} />
                        {row.vaultId && row.vaultUri && (
                          <span className={classes.scopeText}>scope: {row.name}</span>
                        )}
                        {otherBindings.length > 0 && (
                          <Tooltip title={`Also used by: ${otherBindings.join(', ')}`}>
                            <span className={classes.scopeText}>
                              shared with {otherBindings.length} other app{otherBindings.length > 1 ? 's' : ''}
                            </span>
                          </Tooltip>
                        )}
                      </>
                    ) : otherBindings.length > 0 ? (
                      <Tooltip title={`Used by: ${otherBindings.join(', ')}`}>
                        <Chip
                          label={`Shared (${otherBindings.length})`}
                          size="small"
                          className={classes.unboundChip}
                        />
                      </Tooltip>
                    ) : (
                      <Chip label="Available" size="small" className={classes.unboundChip} />
                    )}
                  </TableCell>
                  <TableCell className={classes.actionCell}>
                    {isActioning ? (
                      <CircularProgress size={20} />
                    ) : isBoundToThis ? (
                      <Tooltip title="Remove from this application">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LinkOffIcon />}
                          onClick={() => setConfirmUnbind(row)}
                        >
                          Unbind
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Add resource to this application">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<LinkIcon />}
                          onClick={() => handleBind(row)}
                        >
                          Add
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {actionError && (
        <Box px={2} pb={1}>
          <Typography className={classes.errorText}>{actionError}</Typography>
        </Box>
      )}

      {/* Unbind confirmation dialog */}
      <Dialog open={!!confirmUnbind} onClose={() => setConfirmUnbind(null)} maxWidth="xs">
        <DialogTitle>Unbind Resource</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{confirmUnbind?.title || confirmUnbind?.name}</strong> from this application?
            {!!(confirmUnbind?.vaultId && confirmUnbind?.vaultUri) && (() => {
              const otherBindings = (confirmUnbind?.boundComponents ?? []).filter(
                c => c.toLowerCase() !== componentRef.toLowerCase(),
              );
              return otherBindings.length > 0 ? (
                <> The Databricks secret scope will be <strong>kept</strong> because this resource is also used by {otherBindings.length} other app{otherBindings.length > 1 ? 's' : ''}.</>
              ) : (
                <> The Databricks secret scope <strong>{confirmUnbind?.name}</strong> will be deleted from the workspace since no other applications use this resource.</>
              );
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmUnbind(null)}>Cancel</Button>
          <Button onClick={handleUnbindConfirmed} color="secondary" variant="contained">
            Unbind
          </Button>
        </DialogActions>
      </Dialog>
    </InfoCard>
  );
}
